import { omitBy } from 'lodash-es';
import ms from 'ms';
import { Action, ExternalActionData } from '@ui/utils/ActivityManager/Action';
import {
  isDashboardLogTypeInput,
  StudyEventAndSessionRequestInput,
  StudyEventRequestInput,
} from '@ui/utils/ActivityManager/type';
import { Event, isSpan } from '@ui/utils/ActivityManager/EventCollector';
import { Device } from '@ui/utils/ActivityManager/Device';
import { Storage } from './Storage';
import { Span } from './Span';
import { toDateISO, toDurationISO } from './utils/Time';
import { DashboardLogTypeInput } from '@mildang/graphql-artifact';

import { createApolloClient } from '@ui/utils/createApolloClient';
import { gql, NormalizedCacheObject } from '@apollo/client';
import { ApolloClient } from '@apollo/client/core';
import {
  EventProcessor_StudyModuleInstanceFragment,
  EventProcessor_StudyModuleInstanceFragmentDoc,
  EventProcessorDocument,
  EventProcessorQuery,
  EventProcessorQueryVariables,
} from './EventProcessor.generated';
import { APP_SESSION_EVENTS } from './constants';
import { addAction } from '@shared/utils/apm';

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;

const getApolloClient = () => {
  if (!apolloClient) {
    apolloClient = createApolloClient({
      wsURL: process.env.NEXT_PUBLIC_WEB_SOCKET_URL as string,
      option: {
        uri: process.env.NEXT_PUBLIC_GRAPHQL_URL,
      },
    });
  }
  return apolloClient;
};

const fetchStudyModuleInstance = async (
  id: string,
): Promise<EventProcessor_StudyModuleInstanceFragment | null> => {
  const client = getApolloClient();
  const cashedStudyModuleInstance = client.readFragment({
    id: client.cache.identify({
      id,
      __typename: 'StudyModuleInstance',
    }),
    fragment: EventProcessor_StudyModuleInstanceFragmentDoc,
  });

  if (cashedStudyModuleInstance) {
    return cashedStudyModuleInstance;
  } else {
    const { data } = await client.query<EventProcessorQuery, EventProcessorQueryVariables>({
      query: EventProcessorDocument,
      errorPolicy: 'ignore',
      variables: { id },
    });
    return data?.studyModuleInstanceById ?? null;
  }
};

const MIM_STUDY_START_DURATION_SEC = ms('0.4s') / 1000;

interface EventProcessorInput<T extends StudyEventAndSessionRequestInput = StudyEventAndSessionRequestInput> {
  storage: Storage<T>;
}

export class EventProcessor<T extends StudyEventAndSessionRequestInput = StudyEventAndSessionRequestInput> {
  storage: Storage<T>;

  constructor(input: EventProcessorInput<T>) {
    this.storage = input.storage;
  }

  private convertSpanToStudyEventLog(span: Span): Partial<T> | null {
    if (!span.endTime) {
      return null;
    }

    return {
      eventId: span.spanId,
      eventType: span.data.type,
      eventPublishedAt: toDateISO(span.startTime),
      duration: span.duration ? toDurationISO(span.duration) : undefined,
    } as T;
  }

  private convertActionToStudyEventLog(action: Action<ExternalActionData>): Partial<T> | null {
    return {
      eventId: action.actionId,
      eventType: action.data.type,
      eventPublishedAt: toDateISO(action.timestamp),
    } as T;
  }

  convertEventToStudyEventLog(event: Event): T | null {
    if (event.data.type === '__INTERNAL_STUDY_TIME_EXCHNAGE') {
      return null;
    }

    if (!event.activitySessionId || !event.view?.viewId) {
      // 세션/뷰 식별자 누락으로 study-event-log 변환 실패 → storage에 안 쌓임 (축 B)
      addAction('study_event_dropped', {
        reason: 'missing-session-or-view',
        eventType: event.data.type,
      });
      return null;
    }

    // @ts-ignore
    let eventPayload = {
      sessionId: event.activitySessionId,
      viewId: event.view?.viewId,
      // userId: event.userId,
      userId: Number(event.userId),
      parentEventId: event.parentSpanId,
      ...(event instanceof Span && this.convertSpanToStudyEventLog(event)),
      ...(event instanceof Action && this.convertActionToStudyEventLog(event as any)),
    } as T;

    if (
      isSpan(event) &&
      event.data.type === 'STUDY_TIME_SPENT' &&
      event.duration &&
      event.duration <= MIM_STUDY_START_DURATION_SEC
    ) {
      if (
        !this.storage
          .getStorageData()
          .find(
            (item) =>
              !isDashboardLogTypeInput(item) &&
              (item as StudyEventRequestInput).parentEventId === event.spanId,
          )
      ) {
        return null;
      }
    }

    if (
      event.data.type === 'SUBMIT' ||
      event.data.type === 'STUDY_TIME_SPENT' ||
      event.data.type === 'FINISH_EVALUATION'
    ) {
      eventPayload = {
        ...eventPayload,
        studyModuleInstanceId: event.data?.studyModuleInstanceId,
        studyUnitInstanceId: event.data?.studyUnitInstanceId,
        studyActivityInstanceId: event.data?.studyActivityInstanceId,
      };
    }
    // TODO
    return omitBy(eventPayload as unknown as T, (value) => value === undefined) as T;
  }
  convertEventToAppSession = async (event: Event) => {
    if (!event.activitySessionId || !event.view?.viewId || !APP_SESSION_EVENTS.includes(event.data.type)) {
      return null;
    }

    const newStudyModuleInstanceId = event.view.query?.studyModuleInstanceId;
    const studyModuleInstance = newStudyModuleInstanceId
      ? await fetchStudyModuleInstance(newStudyModuleInstanceId)
      : null;

    const eventPayload = {
      type: 'STUDENT_CURRENT_PAGE',
      createdAt: event.view.startedAt ? toDateISO(event.view.startedAt) : new Date().toISOString(),
      groupId: studyModuleInstance?.feed.groupId,
      items: {
        title: document.title,
        connectionState: 'ON',
        device: navigator.userAgent,
        activityTime: '00:00:00',
        view: {
          urlPathGroup: event.view.pathname,
          params: {
            ...event.view.query,
          },
        },
      },
    } as DashboardLogTypeInput & {
      view?: any;
    };

    return eventPayload;
  };

  convertDeviceToDto(device: Device) {
    return {
      id: device.deviceId,
      ua: device.ua,
      type: device.device.type,
      os: device.os.name,
      browser: device.browser.name,
    };
  }
}

gql`
  fragment EventProcessor_StudyModuleInstance on StudyModuleInstance {
    id
    feed {
      id
      groupId
    }
  }

  query EventProcessor($id: ID!) {
    studyModuleInstanceById(id: $id) {
      id
      ...EventProcessor_StudyModuleInstance @unmask
    }
  }
`;
